import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useForm, FormProvider } from 'react-hook-form'
import styled from '@emotion/styled'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import useGetVendorProductDetailQuery from '@src/hooks/queries/product/useGetVendorProductDetailQuery'

import FormSection from './components/FormSection'
import FieldBlock from './components/FieldBlock'
import ThumbnailForm from './components/ThumbnailForm'

const productFormSchema = Yup.object({
	title: Yup.string().required('제품명을 입력해주세요.'),
	isOptionExist: Yup.boolean(),
	price: Yup.number().when('isOptionExist', {
		is: false,
		then: (schema) =>
			schema
				.typeError('숫자만 입력 가능해요.')
				.min(1, '최소 입력 가능한 숫자는 1이에요.')
				.required('가격을 설정해 주세요.'),
		otherwise: (schema) =>
			schema
				.transform((_, val: unknown) => (typeof val === 'number' ? val : null))
				.nullable(true)
				.optional(),
	}),
	challengePrice: Yup.number().when('isOptionExist', {
		is: false,
		then: (schema) =>
			schema
				.typeError('숫자만 입력 가능해요.')
				.min(1, '최소 입력 가능한 숫자는 1이에요.')
				.required('가격을 설정해 주세요.'),
		otherwise: (schema) =>
			schema
				.transform((_, val) => (typeof val === 'number' ? val : null))
				.nullable(true)
				.optional(),
	}),
	type: Yup.boolean(),
	challenge: Yup.object({
		title: Yup.string()
			.min(0, '투두리스트 이름을 기입해 주세요.')
			.required('투두리스트 이름을 기입해 주세요.'),
		minAuth: Yup.number()
			.typeError('숫자만 입력 가능해요.')
			.min(1, '최소 입력 가능한 숫자는 1이에요.'),
		period: Yup.number()
			.typeError('숫자만 입력 가능해요.')
			.min(1, '최소 입력 가능한 숫자는 1이에요.'),
	}),
	shipping: Yup.object({
		courier: Yup.object().required('배송사를 선택해주세요.'),
		shippingDay: Yup.number()
			.typeError('숫자만 입력 가능해요.')
			.min(1, '최소 입력 가능한 숫자는 1이에요.'),
		shippingMsg: Yup.string()
			.min(1, '배송 안내문을 기입해 주세요.')
			.required('배송 안내문을 기입해 주세요.'),
		price: Yup.number()
			.typeError('숫자만 입력 가능해요.')
			.min(0, '최소 입력 가능한 숫자는 0이에요.'),
		otherPrice: Yup.number()
			.typeError('숫자만 입력 가능해요.')
			.min(0, '최소 입력 가능한 숫자는 0이에요.'),
		jejuPrice: Yup.number()
			.typeError('숫자만 입력 가능해요.')
			.min(0, '최소 입력 가능한 숫자는 0이에요.'),
		refundPrice: Yup.number()
			.typeError('숫자만 입력 가능해요.')
			.min(0, '최소 입력 가능한 숫자는 0이에요.'),
		refundMsg: Yup.string()
			.min(1, '환불 규정 안내문을 기입해주세요.')
			.required('환불 규정 안내문을 기입해주세요.'),
	}),
	productVariants: Yup.array().when('isOptionExist', {
		is: true,
		then: (schema) =>
			schema.of(
				Yup.object({
					name: Yup.string().required('이름을 설정해주세요'),
				}),
			),
	}),
	productVariantOptions: Yup.array().when('isOptionExist', {
		is: true,
		then: (schema) =>
			schema.of(
				Yup.object({
					name: Yup.string().required('이름을 설정해주세요'),
				}),
			),
	}),
	productSkus: Yup.array().when('isOptionExist', {
		is: true,
		then: (schema) =>
			schema.of(
				Yup.object({
					price: Yup.number()
						.typeError('가격을 설정해 주세요.')
						.min(1, '최소 숫자는 1 입니다.')
						.required('가격을 설정해 주세요.'),
					challengePrice: Yup.number()
						.typeError('가격을 설정해 주세요.')
						.min(1, '최소 숫자는 1 입니다.')
						.required('가격을 설정해 주세요.'),
				}),
			),
	}),
	bannerFile: Yup.mixed().required('파일을 선택해주세요'),
	descriptionFiles: Yup.array()
		.of(Yup.mixed().required('파일을 선택해주세요'))
		.min(1, '파일을 선택해주세요'),
	sampleProofFiles: Yup.array()
		.of(Yup.mixed().required('파일을 선택해주세요'))
		.min(1, '파일을 선택해주세요'),
	bannerBase64: Yup.string(),
}).required()

const ProductEditPage = () => {
	const { productNo } = useParams()
	const isEditMode = useMemo(() => !!productNo, [productNo])

	const { data, isLoading } = useGetVendorProductDetailQuery(
		{ productNo: productNo || '' },
		{
			enabled: !!isEditMode,
		},
	)

	const defaultValues = useMemo(() => {
		if (!data) return {}
		const detailInfo = data.data.product
		if (!detailInfo) return {}
		return {
			title: detailInfo.title,
		}
	}, [data])

	const methods = useForm({
		defaultValues: {
			...defaultValues,
		},
		resolver: yupResolver(productFormSchema),
		mode: 'onTouched',
	})

	const onSubmit = (fieldVal: unknown) => {
		console.log(fieldVal)
	}

	if (isLoading) return <div>...loading</div>
	return (
		<Block>
			<Title>상품 등록</Title>
			<FormProvider {...methods}>
				<Form onSubmit={methods.handleSubmit(onSubmit)}>
					<FormSection title="상품 정보">
						<FieldBlock label="대표 이미지">
							<ThumbnailForm />
						</FieldBlock>
						<FieldBlock label="상품명">
							<input />
						</FieldBlock>
						<FieldBlock label="일반 구매 가격">
							<input />
						</FieldBlock>
						<FieldBlock label="챌린지 구매 가격">
							<input />
						</FieldBlock>
					</FormSection>
					<FormSection title="상품 정보">asd</FormSection>
					<FormSection title="상품 정보">asd</FormSection>
					<FormSection title="챌린지 정보">asd</FormSection>
				</Form>
			</FormProvider>
		</Block>
	)
}

const Block = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	padding-left: 3rem;
	padding-right: 3rem;
	padding-bottom: 3rem;
	margin-left: 11rem;
	overflow-y: scroll;
`

const Title = styled.h1`
	font-size: 2.4rem;
	font-weight: 700;
`

const Form = styled.form`
	margin-top: 5rem;
	width: 70%;
`

export default ProductEditPage
